/**
 * Client-side scrubbing, applied before anything leaves the browser.
 *
 * This is not optional here: the admin console is the one frontend whose forms
 * routinely contain another person's PII **and** a live credential mid-rotation
 * — a mobile number, a one-time code, a gateway secret, a configuration value.
 * None of them may ride along in `message` or `stack`.
 *
 * The patterns cover **both digit sets**, because an operator types on an
 * Arabic keyboard and the resulting string carries Arabic-Indic digits that an
 * ASCII-only pattern walks straight past.
 */

const REDACTED = '[محجوب]';

const ASCII_DIGIT = '0-9';
const ARABIC_INDIC_DIGIT = '٠-٩۰-۹';
const ANY_DIGIT = `[${ASCII_DIGIT}${ARABIC_INDIC_DIGIT}]`;

const PATTERNS: RegExp[] = [
  // Yemeni mobile numbers, international and local shapes, either digit set.
  new RegExp(`(?:\\+?967|00967)[\\s-]?${ANY_DIGIT}{9,10}`, 'g'),
  // The retired Iraqi market: rows written before the market correction still
  // exist, and a shape the scrubber stops recognising is one it stops masking.
  new RegExp(`(?:\\+?964|00964)[\\s-]?${ANY_DIGIT}{9,10}`, 'g'),
  new RegExp(`\\b0${ANY_DIGIT}{9,10}\\b`, 'g'),
  // One-time codes: a standalone run of 4–8 digits.
  new RegExp(
    `(?<![${ASCII_DIGIT}${ARABIC_INDIC_DIGIT}])${ANY_DIGIT}{4,8}(?![${ASCII_DIGIT}${ARABIC_INDIC_DIGIT}])`,
    'g',
  ),
  // E-mail addresses.
  /[^\s@]+@[^\s@]+\.[^\s@]+/g,
  // Token- and key-shaped strings: a long unbroken run of credential alphabet.
  /\b[A-Za-z0-9_\-.]{24,}\b/g,
  // Anything explicitly labelled as a secret in a serialised object.
  /("?(?:password|secret|token|api_?key|credential)"?\s*[:=]\s*)("?[^",}\s]+"?)/gi,
];

export function scrub(input: string): string {
  return PATTERNS.reduce<string>(
    (text, pattern) =>
      text.replace(pattern, (_match: string, prefix?: string) =>
        prefix ? `${prefix}${REDACTED}` : REDACTED,
      ),
    input,
  );
}
